import type { IPlatformLogger, PlatformConfig, ServicesLoaders } from '@wix/thunderbolt-symbols'
import { TestServiceDefinition } from '@wix/thunderbolt-services'
import { NamedSignalsDefinition } from '@wix/viewer-service-named-signals/definition'
import { SdkStateDefinition } from '@wix/viewer-service-sdk-state/definition'
import namedSignalsServiceConfig from './configs/namedSignalsServiceConfig'
import linkUtilsServiceConfig from './configs/linkUtilsServiceConfig'
import { StyleUtilsDefinition } from '@wix/viewer-service-style-utils/definition'
import { FedopsLoggerDefinition } from '@wix/viewer-service-fedops-logger/definition'
import { UrlDefinition } from '@wix/viewer-service-url/definition'
import { EnvironmentDefinition } from '@wix/viewer-service-environment/definition'
import type { ServiceConfigFactory } from '../types'
import { LinkUtilsDefinition } from '@wix/viewer-service-link-utils/definition'
import { WixClientDefinition } from '@wix/viewer-service-wix-client/definition'
import { ConsentPolicyDefinition } from '@wix/viewer-service-consent-policy/definition'
import { BusinessLoggerDefinition } from '@wix/viewer-service-business-logger/definition'
import { TranslationsDefinition } from '@wix/viewer-service-translations/definition'
import { InteractionsDefinition } from '@wix/viewer-service-interactions/definition'
import { SessionManagerDefinition } from '@wix/viewer-service-session-manager/definition'
import { SiteThemeDefinition } from '@wix/viewer-service-site-theme/definition'
import { PageContextDefinition } from '@wix/viewer-service-page-context/definition'
import { TopologyDefinition } from '@wix/viewer-service-topology/definition'
import { MappersLegacyDefinition } from '@wix/mappers-legacy-service/definition'
import { noopServiceImplementations } from './noopImplementations'

export const serviceLoaders: ServicesLoaders = {
	[TestServiceDefinition as string]: () => import('feature-test-service/service' /* webpackChunkName: "platformTestService" */),
	[NamedSignalsDefinition as string]: () => import('feature-named-signals-service/service' /* webpackChunkName: "platformNamedSignalsService" */),
	[StyleUtilsDefinition as string]: () => import('feature-style-utils-service/service' /* webpackChunkName: "platformStyleUtilsService" */),
	[FedopsLoggerDefinition as string]: () => import('feature-fedops-logger-service/service' /* webpackChunkName: "platformFedopsLoggerService" */),
	[EnvironmentDefinition as string]: () => import('feature-environment-service/service' /* webpackChunkName: "platformEnvironmentService" */),
	[TranslationsDefinition as string]: () => import('feature-translations-service/service' /* webpackChunkName: "platformTranslationsService" */),
	[InteractionsDefinition as string]: () => import('feature-interactions/service' /* webpackChunkName: "platformInteractionsService" */),
	[SiteThemeDefinition as string]: () => import('feature-site-theme-service/service' /* webpackChunkName: "platformSiteThemeService" */),
	[PageContextDefinition as string]: () => import('feature-page-context-service/service' /* webpackChunkName: "platformPageContextService" */),
	[TopologyDefinition as string]: () => import('feature-topology-service/service' /* webpackChunkName: "platformTopologyService" */),
	[ConsentPolicyDefinition as string]: () => import('feature-consent-policy-service/service' /* webpackChunkName: "platformConsentPolicyService" */),
	[MappersLegacyDefinition as string]: () => import('feature-mappers-legacy-service/service' /* webpackChunkName: "platformMappersLegacyService" */),
	[LinkUtilsDefinition as string]: () => import('feature-link-utils-service/service' /* webpackChunkName: "platformLinkUtilsService" */),
	[SdkStateDefinition as string]: () => import('feature-sdk-state-service/service' /* webpackChunkName: "platformSdkStateService" */),
}

export const serviceConfigs: {
	[serviceDefinition: string]: ServiceConfigFactory
} = {
	[NamedSignalsDefinition as string]: ({ modelsApi, bootstrapData, viewerHandlers }) => namedSignalsServiceConfig(modelsApi, bootstrapData, viewerHandlers),
	[LinkUtilsDefinition]: linkUtilsServiceConfig,
}

// TODO - remove when implemented and stable in TB
export const noopServiceDefinitions = [WixClientDefinition as string, BusinessLoggerDefinition as string, UrlDefinition as string, SessionManagerDefinition as string, InteractionsDefinition as string]

export const getServiceDefinitionToFactoryPromise = async (serviceDefinitionToConfig: PlatformConfig | undefined, logger: IPlatformLogger) =>
	Promise.all(
		Array.from(new Set([...Object.keys(serviceDefinitionToConfig!), ...noopServiceDefinitions])).map((serviceDefinition) => {
			// TODO - remove when implemented and stable in TB
			if (noopServiceDefinitions.includes(serviceDefinition)) {
				return Promise.resolve([
					serviceDefinition,
					{
						definition: serviceDefinition,
						impl: () => noopServiceImplementations[serviceDefinition],
					},
				])
			}
			const factoryGetter = serviceLoaders[serviceDefinition]
			if (!factoryGetter) {
				logger.captureError(new Error(`Service ${serviceDefinition} is not supported`), {
					tags: {
						feature: 'platform',
					},
				})
				return Promise.resolve([serviceDefinition, { impl: () => ({}) }])
			}
			return factoryGetter().then((implementation) => [serviceDefinition, implementation])
		})
	).then((servicesArray) => Object.fromEntries(servicesArray))
